const getUrlBase = () => {
    const match = location.pathname.match(/^.*Med(?:Student)?Portal\//i);
    return match && match[0] || "/";
};

const escapeTextForHtml = text => {
    const container = document.createElement('span');
    container.innerText = text;
    return container.innerHTML;
};

const namespace = (...args) => {
    let baseObject;
    let name;
    if (args.length === 1) {
        baseObject = null;
        name = args[0];
    } else if (args.length === 2) {
        baseObject = args[0];
        name = args[1];
    } else {
        throw new Error('Unsupported arguments');
    }

    let object = baseObject || window;

    if (name.length === 0) {
        return object;
    }

    for (const component of name.split('.')) {
        if (component.length === 0) {
            throw new Error('Name contains an empty path component');
        }

        let nextObject = object[component];
        if (nextObject == null) {
            nextObject = { };
            object[component] = nextObject;
        }

        object = nextObject;
    }

    return object;
};

/**
 * 
 * @param {ArrayLike<HTMLInputElement>} elements
 */
const allowRadioButtonDeselection = elements => {
    const elementsGroupedByName = arrayGroupBy(elements, element => element.name);
    for (const elements of elementsGroupedByName.values()) {
        let previouslyCheckedElement = elements.find(element => element.checked);

        for (const element of elements) {
            element.addEventListener("click", (/* event */) => {
                if (element === previouslyCheckedElement) {
                    element.checked = false;
                    element.dispatchEvent(new Event('input'));

                    previouslyCheckedElement = null;
                } else {
                    previouslyCheckedElement = element;
                }
            });
        }
    }
};

const attachTextSelectingClickHandler = input => {
    const inputElement = getHtmlElement(input);

    let lastMouseDownX = null;
    let lastMouseDownY = null;

    const handleMouseDown = event => {
        lastMouseDownX = event.clientX;
        lastMouseDownY = event.clientY;
    };

    const handleMouseUp = event => {
        if (lastMouseDownX === null) {
            return;
        }

        const xDelta = Math.abs(event.clientX - lastMouseDownX);
        const yDelta = Math.abs(event.clientY - lastMouseDownY);
        if (xDelta >= 2 || yDelta >= 2) {
            return;
        }

        inputElement.select();
    };

    inputElement.addEventListener('mousedown', handleMouseDown);
    inputElement.addEventListener('mouseup', handleMouseUp);

    return () => {
        inputElement.removeEventListener('mousedown', handleMouseDown);
        inputElement.removeEventListener('mouseup', handleMouseUp);
    };
};

const getLowestSiblingElementAfter = element => {
    let currentLowestElement = element;

    let currentElement = currentLowestElement;
    while (true) {
        currentElement = currentElement.nextElementSibling;
        if (currentElement == null) {
            break;
        }

        const currentLowestElementRect = currentLowestElement.getBoundingClientRect();
        const currentLowestElementBottom = currentLowestElementRect.bottom;

        const currentElementRect = currentElement.getBoundingClientRect();
        const currentElementBottom = currentElementRect.bottom;

        if (currentElementBottom > currentLowestElementBottom) {
            currentLowestElement = currentElement;
        }
    }

    return currentLowestElement;
};

const objectIsString = obj => Object.prototype.toString.call(obj) === "[object String]";

const throwIfNull = (obj, paramName) => {
    if (obj == null) {
        throw new Error(`${paramName || 'Argument'} must not be null or undefined`);
    }
};

/**
 * @param {HTMLElement|String|jQuery} value value
 * @returns {HTMLElement} element
 */
const getHtmlElement = value => {
    if (value == null) {
        throw new Error('value must not be null or undefined');
    }

    if (value instanceof HTMLElement) {
        return value;
    }

    if (objectIsString(value)) {
        const element = document.getElementById(value);
        if (element == null) {
            throw new Error(`No such element with ID ${value}`);
        }

        return element;
    }

    if (value instanceof jQuery) {
        const element = value[0];
        if (element == null) {
            console.error({ jQueryValue: value });
            throw new Error('jQuery object was empty');
        }

        const secondElement = value[1];
        if (secondElement !== undefined) {
            console.error({ jQueryValue: value });
            throw new Error('jQuery object contains more than one element');
        }

        return element;
    }

    console.error({ value });
    throw new Error('value must be HtmlElement, string, or jQuery');
};

/**
 * @param {HTMLElement|String|jQuery} value value
 * @returns {HTMLElement|null} element
 */
const tryGetHtmlElement = value => {
    if (value == null) {
        return null;
    }

    if (value instanceof HTMLElement) {
        return value;
    }

    if (objectIsString(value)) {
        const element = document.getElementById(value);
        if (element == null) {
            return null;
        }

        return element;
    }

    if (value instanceof jQuery) {
        const element = value[0];
        if (element == null) {
            console.log({ jQueryValue: value });
            console.log('jQuery object was empty');
            return null;
        }

        const secondElement = value[1];
        if (secondElement !== undefined) {
            console.log({ jQueryValue: value });
            console.log('jQuery object contains more than one element');
            return null;
        }

        return element;
    }

    return null;
};

const alignItemsInContainers = (referenceContainer, referenceItem, targetItem) => {
    const referenceContainerElement = getHtmlElement(referenceContainer);
    const referenceItemElement = getHtmlElement(referenceItem);
    const targetItemElement = getHtmlElement(targetItem);

    targetItemElement.style.marginTop = `${referenceItemElement.getBoundingClientRect().y - referenceContainerElement.getBoundingClientRect().y}px`;
};

const FileSize = Class.create(({ field, method, property, constructor }) => {
    const kilo = 1024;

    const _byteCount = field();

    constructor((self, byteCount) => _byteCount.set(self, Math.round(byteCount || 0)));
    method('static bytes', byteCount => new FileSize(byteCount));
    method('static kilobytes', kilobyteCount => FileSize.bytes(kilobyteCount * kilo));
    method('static megabytes', megabyteCount => FileSize.kilobytes(megabyteCount * kilo));
    method('static gigabytes', gigabyteCount => FileSize.megabytes(gigabyteCount * kilo));

    property('byteCount', { get: self => _byteCount.get(self) });
    property('kilobyteCount', { get: self => self.byteCount / kilo });
    property('megabyteCount', { get: self => self.kilobyteCount / kilo });
    property('gigabyteCount', { get: self => self.megabyteCount / kilo });

    const round = (num, fractionDigits = 2) => num.toFixed(fractionDigits).replace(/\.?0+$/, '');
    method('toByteString', self => `${self.byteCount} B`);
    method('toKilobyteString', self => `${round(self.kilobyteCount)} KB`);
    method('toMegabyteString', self => `${round(self.megabyteCount)} MB`);
    method('toGigabyteString', self => `${round(self.gigabyteCount)} MB`);
    method('toString', self => self.toByteString());
});

const UrlBuilder = Class.create(({ field, method, constructor }) => {
    const _base = field();
    const _parameters = field();

    constructor((self, base) => {
        _base.set(self, base || '');
        _parameters.set(self, { });
    });

    method('set', (self, key, value) => {
        const parameters = _parameters.get(self);

        if (!objectIsString(key)) {
            throw new Error("key must be a string");
        }
        if (key.length === 0) {
            throw new Error("key must not be empty");
        }
        if (encodeURIComponent(key) != key) { // != to compare string and String
            throw new Error("key must not contain invalid characters");
        }
        
        parameters[key] = value;
        return self;
    });

    method('build', self => {
        const base = _base.get(self);
        const parameters = _parameters.get(self);

        const queryString = Object.entries(parameters)
            .map(([key, valueObj]) => ({
                key,
                value: (valueObj != null) && valueObj.toString() || null
            }))
            .filter(({ value }) => value != null && value.length > 0)
            .map(({ key, value }) => `${key}=${encodeURIComponent(value)}`)
            .join("&");

        if (queryString.length === 0) {
            return base;
        }
        return `${base}?${queryString}`;
    });
});

const getDistinctElements = collection => {
    const distinctArray = [];

    for (const element of collection) {
        if (distinctArray.includes(element)) {
            continue;
        }

        distinctArray.push(element);
    }

    return distinctArray;
};

/**
 * 
 * @param {ArrayLike<TSource>} source
 * @param {function (TSource): TKey} keySelector
 * @returns {Map<TKey, Array<TSource>>}
 * 
 * @template TSource, TKey
 */
const arrayGroupBy = (source, keySelector) => {
    const map = new Map();
    for (const element of source) {
        const key = keySelector(element);

        let grouping = map.get(key);
        if (typeof grouping === 'undefined') {
            grouping = [];
            map.set(key, grouping);
        }

        grouping.push(element);
    }

    return map;
};

Array.prototype.groupBy = function (keySelector) {
    return arrayGroupBy(this, keySelector);
};

const arrayMax = (source, selector) => {
    let didFindElement = false;
    let maxElement = null;
    let maxValue = null;

    for (const element of source) {
        const value = selector == null
            ? element
            : selector(element);

        if (
            !didFindElement
            || value > maxValue
        ) {
            didFindElement = true;
            maxElement = element;
            maxValue = value;
        }
    }

    if (!didFindElement) {
        throw new Error('source contained no elements');
    }

    return { element: maxElement, value: maxValue };
};

const arrayWithIndexes = array => {
    let nextIndex = 0;
    return Array.prototype.map.call(array, element => {
        const currentIndex = nextIndex;
        nextIndex += 1;

        return [currentIndex, element];
    });
};

const showToast = (() => {
    const toastContainer = document.getElementById("ToastContainer");

    let aboveModalCount = 0;
    const handleAboveModalOpen = () => {
        aboveModalCount += 1;
        if (aboveModalCount == 1) {
            toastContainer.style.zIndex = 1051;
        }
    };
    const handleAboveModalClose = () => {
        aboveModalCount -= 1;
        if (aboveModalCount == 0) {
            toastContainer.style.zIndex = 1;
        }
    };

    return ({
        title,
        details,
        body,
        bodyHtml,
        bodyChildNode,
        status,
        hideAfter,
        aboveModal
    } = {}) => {
        if (body != null && bodyHtml != null) {
            throw new Error("Both body and bodyHtml may not be specified");
        }

        let headerBackgroundColorClass;
        let headerForegroundColorClass;
        const bodyBackgroundColorClass = "bg-light";
        const bodyForegroundColorClass = "text-dark";

        if (status === "success") {
            headerBackgroundColorClass = "bg-success";
            headerForegroundColorClass = "text-light";
        } else if (status === "error") {
            headerBackgroundColorClass = "bg-danger";
            headerForegroundColorClass = "text-light";
        } else { // status === "info"
            headerBackgroundColorClass = "bg-primary";
            headerForegroundColorClass = "text-light";
        }

        const toast = document.createElement("div");
        toast.className = "toast";
        toast.setAttribute("role", "alert");
        toast.setAttribute("aria-live", "assertive");
        toast.setAttribute("aria-atomic", true);
        toastContainer.prepend(toast);

        const toastHeader = document.createElement("div");
        toastHeader.className = `toast-header ${headerBackgroundColorClass}`;
        toast.append(toastHeader);

        const toastHeaderLeftArea = document.createElement("div");
        toastHeader.appendChild(toastHeaderLeftArea);

        //const toastImage = document.createElement("img");
        //toastImage.src = "...";
        //toastImage.className = "rounded mr-2";
        //toastImage.alt = "...";
        //toastHeaderLeftArea.append(toastImage);
        //const toastImage = document.createElement("span");
        //toastImage.className = "glyphicon glyphicon-search text-white";
        //toastHeaderLeftArea.append(toastImage);

        if (title != null) {
            const toastTitle = document.createElement("strong");
            toastTitle.className = `mr-3 ${headerForegroundColorClass}`;
            toastTitle.textContent = title;
            toastHeaderLeftArea.append(toastTitle);
        }

        const toastHeaderRightArea = document.createElement("div");
        toastHeader.appendChild(toastHeaderRightArea);

        if (details != null) {
            const toastDetails = document.createElement("small");
            toastDetails.className = "text-muted";
            toastDetails.textContent = details;
            toastHeaderRightArea.append(toastDetails);
        }

        const toastCloseButton = document.createElement("button");
        toastCloseButton.type = "button";
        toastCloseButton.className = `ml-2 mb-1 close ${headerForegroundColorClass}`;
        toastCloseButton.setAttribute("data-dismiss", "toast");
        toastCloseButton.setAttribute("aria-label", "Close");
        toastHeaderRightArea.append(toastCloseButton);

        const toastCloseButtonText = document.createElement("span");
        toastCloseButtonText.setAttribute("aria-hidden", true);
        toastCloseButtonText.textContent = "×";
        toastCloseButton.append(toastCloseButtonText);

        if (body != null || bodyHtml != null || bodyChildNode != null) {
            const toastBody = document.createElement("div");
            toastBody.className = `toast-body ${bodyBackgroundColorClass}`;
            toast.append(toastBody);

            const toastBodyText = document.createElement("span");
            toastBodyText.className = bodyForegroundColorClass;
            if (body != null) {
                toastBodyText.innerText = body;
            } else if (bodyHtml != null) {
                toastBodyText.innerHTML = bodyHtml;
            } else { // bodyChildNode != null
                toastBodyText.append(bodyChildNode);
            }
            toastBody.append(toastBodyText);
        }

        const toastController = $(toast);

        const toastLibraryOptions = {
            animation: true
        };

        if (hideAfter == null) {
            toastLibraryOptions.autohide = false;
        } else {
            toastLibraryOptions.autohide = true;
            toastLibraryOptions.delay = hideAfter;
        }

        toastController.toast(toastLibraryOptions);

        if (aboveModal) {
            handleAboveModalOpen();
            toastController.on('hidden.bs.toast', handleAboveModalClose);
        }

        toastController.toast('show');
    };
})();

const showModal = (() => {
    const modalContainer = document.getElementById('ModalContainer');
    if (modalContainer === null) {
        throw new Error('Could not find ModalContainer element on document');
    }

    const setContent = (element, text, html, node) => {
        if (node != null) {
            element.append(node);
        } else if (html != null) {
            element.innerHTML = html;
        } else if (text != null) {
            element.innerText = text;
        }
    };

    return ({
        titleText,
        bodyText,
        bodyHtml,
        bodyNode,
        defaultCloseButtonText,
        buttons,
        onHidden
    } = { }) => {
        $(modalContainer).empty();
        $('body > .modal-backdrop').remove();

        const modal = document.createElement('div');
        modalContainer.append(modal);
        modal.className = 'modal fade';
        modal.tabIndex = -1;
        const $modal = $(modal);
        {
            const dialog = document.createElement('div');
            modal.append(dialog);
            dialog.className = 'modal-dialog';
            {
                const content = document.createElement('div');
                dialog.append(content);
                content.className = 'modal-content';
                {
                    const header = document.createElement('div');
                    content.append(header);
                    header.className = 'modal-header';
                    {
                        const title = document.createElement('h5');
                        header.append(title);
                        title.className = 'modal-title';
                        title.innerText = titleText || 'Title';
    
                        const closeButton = document.createElement('button');
                        header.append(closeButton);
                        closeButton.type = 'button';
                        closeButton.className = 'close';
                        closeButton.setAttribute('data-dismiss', 'modal');
                        {
                            const cross = document.createElement('span');
                            closeButton.append(cross);
                            closeButton.innerHTML = '&times;';
                        }
                    }

                    const body = document.createElement('div');
                    content.append(body);
                    body.className = 'modal-body';
                    setContent(body, bodyText, bodyHtml, bodyNode);

                    const footer = document.createElement('div');
                    content.append(footer);
                    footer.className = 'modal-footer';
                    {
                        if (buttons == null || buttons.length === 0 || defaultCloseButtonText != null) {
                            const closeButton = document.createElement('button');
                            footer.append(closeButton);
                            closeButton.type = 'button';
                            closeButton.className = 'btn btn-secondary';
                            closeButton.setAttribute('data-dismiss', 'modal');
                            closeButton.innerText = defaultCloseButtonText || 'Close';
                        }

                        if (buttons != null)
                        {
                            const modalActions = {
                                toggle: () => $modal.modal('toggle'),
                                show: () => $modal.modal('show'),
                                hide: () => $modal.modal('hide'),
                                handleUpdate: () => $modal.modal('handleUpdate'),
                                dispose: () => $modal.modal('dispose')
                            };

                            let defaultButtonExists = false;

                            for (const { text, className, handleClick, isDefault } of buttons) {
                                const button = document.createElement('button');
                                footer.append(button);
                                button.type = 'button';
                                button.className = className || 'btn btn-primary';
                                button.innerText = text || 'Button';

                                if (handleClick != null) {
                                    button.addEventListener('click', mouseEvent => handleClick(modalActions, { mouseEvent }));
                                }

                                const isEffectivelyDefault = (buttons.length === 1 && typeof isDefault === 'undefined') ? true : isDefault;
                                if (isEffectivelyDefault) {
                                    if (defaultButtonExists) {
                                        throw new Error(`Cannot have multiple default buttons ("${text}")`);
                                    }
                                    defaultButtonExists = true;

                                    modal.addEventListener('keyup', keyboardEvent => {
                                        if (keyboardEvent.key === 'Enter') {
                                            handleClick(modalActions, { keyboardEvent });
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
    
        $modal.modal();

        if (onHidden != null) {
            $modal.on('hidden.bs.modal', onHidden);
        }

        return $modal;
    };
})();

function stringIsNullOrWhiteSpace(string) {
    if (string == null) {
        return true;
    }

    const stringContainsANonWhitespaceCharacter = /\S/.test(string);
    return !stringContainsANonWhitespaceCharacter;
}

function arrayRemove(array, element) {
    const index = array.indexOf(element);
    if (index === -1) {
        throw new Error(`Array does not contain element {element}`);
    }

    array.splice(index, 1);
}

/**
 * @param {any[]} array Array
 * @param {number} chunkSize Chunk size
 * @returns {any[]} Chunked array
 */
function arrayChunk(array, chunkSize) {
    const chunks = [];

    let startIndex = 0;
    while (startIndex < array.length) {
        const endIndex = startIndex + chunkSize;
        chunks.push(array.slice(startIndex, endIndex));

        startIndex = endIndex;
    }

    return chunks;
}

function promiseDelay(milliseconds) {
    return new Promise(resolve => {
        setTimeout(resolve, milliseconds);
    });
}

function getBoolFrom3RadioButtons({ trueInput, falseInput, nullInput } = { }) {
    const trueInputElement = getHtmlElement(trueInput);
    const falseInputElement = getHtmlElement(falseInput);
    const nullInputElement = getHtmlElement(nullInput);

    let checkedCount = 0;
    if (nullInputElement.checked) {
        checkedCount++;
    }
    if (falseInputElement.checked) {
        checkedCount++;
    }
    if (trueInputElement.checked) {
        checkedCount++;
    }

    if (checkedCount === 0) {
        throw new Error("None of the nullable bool radio buttons are checked");
    }
    if (checkedCount > 1) {
        throw new Error(`${checkedCount} of the nullable bool radio buttons are checked. Only one may be checked`);
    }

    if (nullInputElement.checked) {
        return null;
    }
    if (falseInputElement.checked) {
        return false;
    }
    // trueRadioButton.checked
    return true;
}

function getBoolFromRadioButtons(trueRadioButtonIdOrElement, falseRadioButtonIdOrElement) {
    const trueRadioButton = getHtmlElement(trueRadioButtonIdOrElement);
    const falseRadioButton = getHtmlElement(falseRadioButtonIdOrElement);

    if (trueRadioButton.checked)
    {
        if (falseRadioButton.checked) {
            throw new Error("Both the true radio button and the false radio button were checked");
        }

        return true;
    }

    if (falseRadioButton.checked) {
        return false;
    }

    return null;
}

function getBoolFromSelect(select, { nullIndex = 0, trueIndex = 1, falseIndex = 2 } = { }) {
    const selectElement = getHtmlElement(select);
    const selectedIndex = selectElement.selectedIndex;
    if (selectedIndex === undefined) {
        throw new Error(`Select ${select} has no selectedIndex property`);
    }

    switch (selectedIndex) {
        case nullIndex: {
            return null;
        }
        case trueIndex: {
            return true;
        }
        case falseIndex: {
            return false;
        }
    }

    throw new Error(`Select ${select}'s selected index (${selectedIndex}) does not match with any indexes`);
}

const getValueIfSelected = (elementOrId, getValue) => {
    const element = getHtmlElement(elementOrId);

    if (element.selectedIndex === 0) {
        return null;
    }

    return getValue(element);
};

const setInputsEnabled = (inputs, enabled) => {
    for (const input of inputs) {
        if (typeof input.disabled === 'undefined') {
            console.log('disabled property is undefined', input);
            continue;
        }

        input.disabled = !enabled;
    }
};

function stripNewLines(string) {
    return string && string.replace(/[\r\n]/g, " ");
}

/**
 * Converts a date given by an HTML input element to a date for the user's time zone.
 * @param {Date} date
 * @returns {Date}
 */
const convertInputDateToLocal = date => {
    if (date == null) {
        return null;
    }

    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
    );
};

function objectComputeIfAbsent(obj, key, computeValue) {
    if (Object(obj) !== obj) {
        throw new Error('obj must be an object');
    }

    let value = obj[key];
    if (typeof value === 'undefined') {
        value = computeValue(key);
        obj[key] = value;
    }

    return value;
}

/**
 * Generates an array that contains one repeated value.
 * @param {T} element The value to be repeated.
 * @param {number} count The number of times to repeat the value in the generated array.
 * @returns {Array<T>}
 * @template T
 */
function arrayRepeat(element, count) {
    const array = [];
    for (let i = 0; i < count; i++) {
        array[i] = element;
    }

    return array;
}

/**
 * Generates a sequence of integral numbers within a specified range. Optionally maps each number with the specified mapping function.
 * @param {number} start The value of the first integer in the sequence.
 * @param {number} count The number of sequential integers to generate.
 * @param {function (number): T} [mapfn=(i: number) => i] A mapping function to call on every element of the array, or null.
 * @returns {Array<T>}
 * @template T
 */
function arrayRange(start, count, mapfn) {
    let realMapfn;
    if (mapfn == null) {
        realMapfn = (value, key) => key + start;
    } else {
        realMapfn = (value, key) => mapfn(key + start, key);
    }

    return Array.from({ length: count }, realMapfn);
}

/**
 * Gets a random element from an array.
 * @param {ArrayLike<T>} items
 * @returns {T}
 * @template T
 */
function getRandomElement(items) {
    return items[Math.floor(Math.random() * items.length)];
}

/**
 * Gets a random string of the specified length
 * @param {ArrayLike<String>} chars
 * @param {number} length
 * @returns {String}
 */
function getRandomString(chars, length) {
    return arrayRange(0, length, _ => getRandomElement(chars)).join('');
}

/**
 * Gets a random HTML element ID compatible with CSS.
 * @returns {String}
 */
function getRandomHtmlId() {
    /*
        ID and NAME tokens must begin with a letter ([A-Za-z]) and may be followed by any number of letters, digits ([0-9]), hyphens ("-"), underscores ("_"), colons (":"), and periods (".").
        https://www.w3.org/TR/html401/types.html#type-name

        In CSS, identifiers (including element names, classes, and IDs in selectors) can contain only the characters [a-zA-Z0-9] and ISO 10646 characters U+00A0 and higher, plus the hyphen (-) and the underscore (_); they cannot start with a digit, two hyphens, or a hyphen followed by a digit. Identifiers can also contain escaped characters and any ISO 10646 character as a numeric code (see next item). For instance, the identifier "B&W?" may be written as "B\&W\?" or "B\26 W\3F".
        https://www.w3.org/TR/CSS2/syndata.html#characters
    */

    const parts = [
        getRandomString('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 1),
        getRandomString('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_', 127) // don't use :.
    ];
    return parts.join('');
}

const isDateSqlCompatable = date => {
    if (date == null) {
        return true;
    }

    if (isNaN(date)) {
        return false;
    }

    const year = date.getFullYear();
    if (year < 1753 || year > 9999) {
        return false;
    }

    return true;
};

const navigate = (base, ...paths) => {
    let position = base;
    for (const path of paths) {
        if (position == null) {
            return position;
        }

        position = path(position);
    }

    return position;
};

const initSelect2 = elementOrId => {
    const select = getHtmlElement(elementOrId);
    const $select = $(select);

    if (typeof $select.select2 === 'undefined') {
        throw new Error('Select2 script not loaded');
    }

    $select.select2();
    $select.on('change', () => select.dispatchEvent(new Event('input')));

    const select2Data = $select.data('select2');

    // Open dropdown if the selection is focused and a key is pressed
    // Populate the search input with the key that was pressed
    select2Data.$selection.on('keypress', event => {
        if (select2Data.isOpen()) {
            // Dropdown is already open
            return;
        }

        $select.select2('open');

        const searchInput = select2Data.dropdown.$search[0];
        searchInput.value = event.key;
        searchInput.dispatchEvent(new Event('change'));
    });
};

const initMonthPicker = elementOrId => {
    const input = getHtmlElement(elementOrId);
    const $input = $(input);

    if (typeof $input.datepicker === 'undefined') {
        throw new Error('bootstrap-datepicker script not loaded');
    }

    const getInitialDate = text => {
        const dateInputFormatMatch = text.match(/^(\d+)-(\d+)(?:-(\d+))?$/);
        if (dateInputFormatMatch != null) {
            return new Date(
                Number(dateInputFormatMatch[1]),
                Number(dateInputFormatMatch[2]) - 1,
                dateInputFormatMatch[3] == null ? 1 : Number(dateInputFormatMatch[3])
            );
        }

        const displayFormatMatch = text.match(/^(\d+)\/(\d+)$/);
        if (displayFormatMatch != null) {
            return new Date(
                Number(displayFormatMatch[2]),
                Number(displayFormatMatch[1]) - 1,
            );
        }
        
        return null;
    };
    
    const initialDate = getInitialDate(input.value);
    
    $input.datepicker({
        autoclose: true,
        clearBtn: true,
        format: 'm/yyyy',
        viewMode: 'months',
        minViewMode: 'months',
        maxViewMode: 'years'
    });
    $input.datepicker('setDate', initialDate);

    let previousDate = initialDate;
    const reportDate = () => {
        const currentDate = $input.datepicker('getDate');
        if (
            (previousDate == null && currentDate == null)
            || (previousDate != null && currentDate != null && previousDate.getTime() === currentDate.getTime())
        ) {
            return;
        }

        previousDate = currentDate;
        input.dispatchEvent(new Event('input'));
    };

    $input.on('changeDate', reportDate);
    input.addEventListener('keyup', reportDate);
};

const TableOrderManager = Class.create(({ field, constructor }) => {
    const _table = field();
    const _createMoveUpButton = field();
    const _createMoveDownButton = field();
    const _persistReorder = field();

    constructor((self, {
        table,
        createMoveUpButton,
        createMoveDownButton,
        persistReorder
    }) => {
        table = getHtmlElement(table);

        _table.set(self, table);
        _createMoveUpButton.set(self, createMoveUpButton);
        _createMoveDownButton.set(self, createMoveDownButton);
        _persistReorder.set(self, persistReorder);

        drawButtons(self);
    });

    const drawButtons = self => {
        const table = _table.get(self);
        const createMoveUpButton = _createMoveUpButton.get(self);
        const createMoveDownButton = _createMoveDownButton.get(self);

        const rows = table.querySelectorAll('tbody tr');

        for (const [index, row] of arrayWithIndexes(rows)) {
            const orderCell = row.querySelector('.order-column');

            $(orderCell).empty();

            const container = document.createElement('div');
            container.className = 'd-flex flex-column';
            orderCell.append(container);

            const upButton = createMoveUpButton();
            container.append(upButton);
            if (index === 0) {
                upButton.disabled = true;
                upButton.style.visibility = 'hidden';
            } else {
                upButton.addEventListener('click', () => handleMove(self, row, -1));
            }


            const downButton = createMoveDownButton();
            container.append(downButton);
            if (index === rows.length - 1) {
                downButton.disabled = true;
                downButton.style.visibility = 'hidden';
            } else {
                downButton.addEventListener('click', () => handleMove(self, row, 1));
            }
        }
    };

    const handleMove = (self, row, positionDelta) => {
        if (positionDelta === 0) {
            return;
        }

        const table = _table.get(self);
        const persistReorder = _persistReorder.get(self);

        const tbody = table.querySelector('tbody');

        if (positionDelta < 0) {
            let rowToInsertBefore = row;
            while (positionDelta < 0) {
                rowToInsertBefore = rowToInsertBefore.previousElementSibling;
                positionDelta++;
            }

            tbody.insertBefore(row, rowToInsertBefore);
        } else { // positionDelta > 0
            let rowToInsertAfter = row;
            while (positionDelta > 0) {
                rowToInsertAfter = rowToInsertAfter.nextElementSibling;
                positionDelta--;
            }

            tbody.insertBefore(row, rowToInsertAfter.nextElementSibling);
        }

        drawButtons(self);
        persistReorder();
    };
});

const WarnOfUnsavedChangesOnCloseHandler = Class.create(({ field, method, property }) => {
    const _inputHasChanged = field();

    method('setChanged', self => {
        if (_inputHasChanged.get(self) === null) {
            window.addEventListener("beforeunload", event => {
                if (!_inputHasChanged.get(self)) {
                    return undefined;
                }

                const warning = "You have unsaved changes.";
                event.preventDefault();
                event.returnValue = warning;
                return warning;
            });
        }

        _inputHasChanged.set(self, true);
    });

    method('setSaved', self => _inputHasChanged.set(self, false));

    property('inputHasChanged', {
        get: self => _inputHasChanged.get(self) === true
    });
});

const UnfocusedInputMasker = Class.create(({ field, method, constructor }) => {
    const _input = field();
    const _isMasked = field(() => false);
    const _boundHandleFocus = field();
    const _boundHandleBlur = field();
    const _disposed = field(() => false);

    constructor((self, input) => {
        input = getHtmlElement(input);

        const boundHandleFocus = focusEvent => handleFocus(self, focusEvent);
        const boundHandleBlur = focusEvent => handleBlur(self, focusEvent);

        input.addEventListener('focus', boundHandleFocus);
        input.addEventListener('blur', boundHandleBlur);

        _input.set(self, input);
        _boundHandleFocus.set(self, boundHandleFocus);
        _boundHandleBlur.set(self, boundHandleBlur);

        setMasked(self, !input.matches(':focus'));
    });

    const handleFocus = (self, focusEvent) => setMasked(self, false);
    const handleBlur = (self, focusEvent) => setMasked(self, true);

    const setMasked = (self, masked) => {
        _isMasked.set(self, masked);
        redraw(self);
    };

    const redraw = self => {
        const input = _input.get(self);
        const isMasked = _isMasked.get(self);

        if (isMasked) {
            input.classList.add('render-as-password');
        } else {
            input.classList.remove('render-as-password');
        }
    };

    method('dispose', self => {
        const input = _input.get(self);
        const boundHandleFocus = _boundHandleFocus.get(self);
        const boundHandleBlur = _boundHandleBlur.get(self);
        const disposed = _disposed.get(self);

        if (disposed) {
            return;
        }

        input.removeEventListener('focus', boundHandleFocus);
        input.removeEventListener('blur', boundHandleBlur);

        setMasked(self, false);

        _disposed.set(self, true);
    });
});

const enableTabInInput = (input, tabText = '    ') => {
    input = getHtmlElement(input);

    const handleKeyDown = event => {
        if (event.keyCode !== 9) { // tab
            return;
        }

        event.preventDefault();
        document.execCommand('insertText', false, tabText);
    };

    input.addEventListener('keydown', handleKeyDown);
    return () => input.removeEventListener('keydown', handleKeyDown);
};

const Persister = Class.create(({ nestedClass, field, property, constructor }) => {
    nestedClass('Stores', Class.create(({ nestedClass, constructor }) => {
        nestedClass('Function', Class.create(({ field, method, constructor }) => {
            const _setValue = field();

            constructor((self, setValue) => _setValue.set(self, setValue));

            method('set', (self, value) => {
                const setValue = _setValue.get(self);
                setValue(value);
            });
        }));

        nestedClass('LocalStorage', Class.create(({ field, method, constructor }) => {
            const _key = field();

            constructor((self, key) => {
                if (!objectIsString(key)) {
                    throw new Error('key must be a string');
                }

                _key.set(self, key);
            });

            method('get', self => {
                const key = _key.get(self);

                const stringified = localStorage.getItem(key);
                if (stringified === null) {
                    return null;
                }

                const obj = JSON.parse(stringified);
                return { value: obj };
            });

            method('set', (self, value) => {
                const key = _key.get(self);

                const stringified = JSON.stringify(value);
                localStorage.setItem(key, stringified);
            });
        }));

        nestedClass('InputElement', Class.create(({ field, method, constructor }) => {
            const _element = field();

            constructor((self, element) => _element.set(self, getHtmlElement(element)));

            method('set', (self, value) => {
                const element = _element.get(self);
                element.value = value;
            });
        }));

        nestedClass('CheckboxElement', Class.create(({ field, method, constructor }) => {
            const _element = field();

            constructor((self, element) => _element.set(self, getHtmlElement(element)));

            method('set', (self, value) => {
                const element = _element.get(self);
                element.checked = value;
            });
        }));

        constructor('private');
    }));

    const _value = field();
    const _hasValue = field(() => false);
    const _stores = field();

    constructor((self, options) => {
        if (options == null) {
            throw new Error('options missing');
        }

        if (!('defaultValue' in options)) {
            throw new Error('options.defaultValue missing');
        }

        const { defaultValue } = options;
        let { stores } = options;

        if (!Array.isArray(stores)) {
            if (stores != null) {
                throw new Error('options.stores must be an array or null');
            }

            stores = [];
        }

        _stores.set(self, stores);

        let value = null;
        let hasValue = false;
        for (const store of stores) {
            if (!store.get) {
                continue;
            }

            const getResult = store.get();
            if (getResult == null || !('value' in getResult)) {
                continue;
            }

            value = getResult.value;
            hasValue = true;
            break;
        }

        if (!hasValue) {
            value = defaultValue;
        }

        valueProp.set(self, value);
    });

    const valueProp = property('value', {
        get: self => _value.get(self),
        set: (self, newValue) => {
            const value = _value.get(self);
            const hasValue = _hasValue.get(self);
            const stores = _stores.get(self);

            if (hasValue && value === newValue) {
                return;
            }

            _value.set(self, newValue);
            _hasValue.set(self, true);

            for (const store of stores) {
                if (!store.set) {
                    continue;
                }

                store.set(newValue);
            }
        }
    });
});

const DomToModelWriter = Class.create(({ constructor, field, method, property }) => {
    const _domIdPrefix = field();
    const _model = field();

    constructor((self, domIdPrefix, existingModel) => {
        _domIdPrefix.set(self, domIdPrefix || '');
        _model.set(self, existingModel || {});
    });

    const setValue = (self, name, getValue) => {
        const model = _model.get(self);

        const components = name.split('.');
        const idPart = components.join('_');

        const value = getValue(idPart);
        namespace(model, components.slice(0, -1).join('.'))[components[components.length - 1]] = value;
    };

    method('setBool', (self, name) => setValue(self, name, idPart => {
        const domIdPrefix = _domIdPrefix.get(self);

        const trueRadioButton = tryGetHtmlElement(`${domIdPrefix}${idPart}_TrueRadioButton`);
        const falseRadioButton = tryGetHtmlElement(`${domIdPrefix}${idPart}_FalseRadioButton`);
        const nullRadioButton = tryGetHtmlElement(`${domIdPrefix}${idPart}_NullRadioButton`);

        const input = tryGetHtmlElement(`${domIdPrefix}${idPart}Input`);

        if (trueRadioButton != null) {
            if (nullRadioButton == null) {
                return getBoolFromRadioButtons(trueRadioButton, falseRadioButton);
            }

            return getBoolFrom3RadioButtons({
                trueInput: trueRadioButton,
                falseInput: falseRadioButton,
                nullInput: nullRadioButton
            });
        }

        if (input != null && 'checked' in input) {
            return input.checked;
        }

        throw new Error('bool type not supported');
    }));
    method('setString', (self, name) => setValue(self, name, idPart => {
        const domIdPrefix = _domIdPrefix.get(self);
        return getValueIfSelected(`${domIdPrefix}${idPart}Input`, e => e.value);
    }));
    method('setDate', (self, name) => setValue(self, name, idPart => {
        const domIdPrefix = _domIdPrefix.get(self);

        const input = getHtmlElement(`${domIdPrefix}${idPart}Input`);
        const $input = $(input);

        if ($input.data().datepicker != null) {
            const date = $(input).datepicker('getDate');
            return date;
        }

        return convertInputDateToLocal(input.valueAsDate);
    }));
    method('setNumber', (self, name) => setValue(self, name, idPart => {
        const domIdPrefix = _domIdPrefix.get(self);
        return getValueIfSelected(`${domIdPrefix}${idPart}Input`, e => Number(e.value));
    }));

    method('set', (self, name, value) => setValue(self, name, idPart => value));

    property('model', { get: self => _model.get(self) });
});
